// Phase 44.06 — Pack onboarding micro-modal (bottom-sheet su mobile).
// Phase 44.13 — Refactor su MobileModal: body-scroll-lock, safe-area, 100dvh.
// Si auto-apre la PRIMA volta che l'utente ha un pack attivo e non ha
// ancora "ack-ato" l'onboarding (user.pack_onboarding_acked_at == null).
// Non aggressivo: no popup, design premium coerente col wallet.
import { useEffect, useState } from "react";
import { Sparkles, CheckCircle2, Clock, Zap, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import MobileModal from "@/components/MobileModal";

export default function PackOnboardingModal({ open, onClose, totalRemaining }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [viewed, setViewed] = useState(false);

  useEffect(() => {
    if (open && !viewed) {
      setViewed(true);
      trackEvent("pack_onboarding_viewed", { total_remaining: totalRemaining || 0 });
    }
  }, [open, viewed, totalRemaining]);

  const doClose = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/me/onboarding/ack", { key: "pack" });
      trackEvent("pack_onboarding_closed", { total_remaining: totalRemaining || 0 });
    } catch { /* best-effort */ }
    setBusy(false);
    onClose?.();
  };

  const seeRides = async () => {
    await doClose();
    navigate("/app/subscription");
  };

  if (!open) return null;

  return (
    <MobileModal
      open={open}
      onClose={doClose}
      testid="pack-onboarding-modal"
      size="md"
      showClose={!busy}
    >
      <div className="space-y-5">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
          <Sparkles className="h-5 w-5" />
        </span>

        <div>
          <p className="overline text-emerald-700">Credito Consegne attivo</p>
          <h2 className="mt-1 font-display text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="pack-onboarding-title">
            Hai attivato {totalRemaining || 10} corse bonus
          </h2>
          <p className="mt-2 text-sm text-dapnow-muted leading-snug">
            Le consegne verranno scalate automaticamente prima del pagamento singolo.
          </p>
        </div>

        <ul className="space-y-2.5">
          <BulletRow Icon={Zap}        text="Utilizzo automatico: nessuna selezione manuale." />
          <BulletRow Icon={Clock}      text="Valide 12 mesi dalla data di acquisto." />
          <BulletRow Icon={CheckCircle2} text="Nessun rinnovo automatico. Acquisto unico." />
        </ul>

        <div className="flex flex-col sm:flex-row gap-2 pt-1">
          <button
            type="button"
            onClick={seeRides}
            disabled={busy}
            data-testid="pack-onboarding-see-rides"
            className="inline-flex items-center justify-center gap-1.5 rounded-2xl border-2 border-dapnow-line bg-white text-dapnow-ink px-4 h-12 text-sm font-bold tracking-tight hover:border-dapnow-ink active:scale-[0.98] transition flex-1 disabled:opacity-50"
          >
            Vedi le mie corse <ChevronRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={doClose}
            disabled={busy}
            data-testid="pack-onboarding-ack"
            className="inline-flex items-center justify-center rounded-2xl bg-emerald-600 text-white px-5 h-12 text-sm font-extrabold tracking-tight hover:bg-emerald-700 active:scale-[0.98] transition shadow-md flex-1 disabled:opacity-50"
          >
            {busy ? "…" : "Ho capito"}
          </button>
        </div>
      </div>
    </MobileModal>
  );
}

function BulletRow({ Icon, text }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 shrink-0 mt-0.5">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <p className="text-sm text-dapnow-ink leading-snug">{text}</p>
    </li>
  );
}
